import type { Translations } from './types';

export const en: Translations = {
  app: {
    title: 'Release Gate',
    version: 'release-board v0.2',
  },
  form: {
    basicInfo: 'Basic Info',
    featureName: 'feature_name',
    releaseDate: 'release_date',
    planningIntent: 'Planning Intent',
    planningPlaceholder: '- Reduce CS report requests\n- Shorten report generation time',
    onePerLine: 'One per line',
    risks: 'Release Risks',
    risksPlaceholder:
      '- Report generation may fail during network instability\n- Not verified on iOS 15 or lower',
    qaStatus: 'QA Verification Status',
    coreScenario: 'core_scenario',
    edgeCases: 'edge_cases',
    select: '--select--',
    passVerified: 'PASS - Verified',
    partialVerified: 'PARTIAL - Partially Verified',
    partialUnverified: 'PARTIAL - Partially Unverified',
    failUnverified: 'FAIL - Unverified',
    evaluating: 'evaluating',
    runEvaluate: '> run evaluate',
    // 파일 업로드
    inputMode: 'Input Mode',
    manualInput: 'Manual Input',
    fileUpload: 'File Upload',
    dragDropFile: '📎 Drag & drop or click to upload',
    supportedFormats: 'Supports PNG, JPG, PDF, Text',
    analyzing: 'Analyzing...',
    extractedItems: 'AI Extracted Items',
    testCaseImport: 'Test Case Import',
    testCaseDragDrop: '📎 Drag test case file here',
    testCaseSupportedFormats: 'Supports CSV, JSON, Markdown',
    testCaseAnalysis: 'Analysis Result',
    totalTestCases: 'Total Test Cases',
    passed: 'Passed',
    clearFile: 'Clear File',
    // 템플릿 & 스크린샷
    downloadTemplate: '📥 Download Template',
    templateDescription: 'Fill in test cases following the format below',
    screenshotUpload: 'Screenshot Upload',
    screenshotDescription: 'Upload app/web screenshots for AI analysis',
    screenshotAnalysisItems: 'AI Analysis Items',
    uiComponents: 'UI Components',
    userFlow: 'User Flow',
    kpiSuggestions: 'KPI Suggestions',
    coreTestCases: 'Core Tests',
    edgeTestCases: 'Edge Tests',
    orDivider: 'or',
    clearAll: 'Clear All',
    // 이탈 확인
    unsavedChangesTitle: 'You have unsaved changes',
    unsavedChangesMessage: 'You have unsaved changes. Are you sure you want to leave?',
    unsavedChangesStay: 'Keep Editing',
    unsavedChangesLeave: 'Continue',
    unsavedChangesDontAskToday: "Don't ask again today",
    // Draft save
    draftSaved: 'Draft saved',
    draftRestoreTitle: 'Draft found',
    draftRestoreMessage: 'Would you like to restore your previous draft?',
    draftRestoreTime: 'Saved at {time}',
    draftRestore: 'Restore',
    draftDiscard: 'Start fresh',
    draftAutoSave: 'Auto-save',
  },
  result: {
    reportTitle: 'RELEASE DECISION REPORT',
    feature: 'FEATURE',
    releaseDate: 'RELEASE_DATE',
    inputData: 'Input Data',
    evaluationResult: 'Evaluation Result',
    confidenceScore: 'CONFIDENCE_SCORE',
    status: 'STATUS',
    judgmentBasis: 'Judgment Basis',
    allItemsGood: 'All planning, risk, and QA items are satisfactory',
    someImprovementNeeded: 'Some items need improvement',
    reviewRecommendations: 'Review recommendations before release decision',
    multipleUnmet: 'Multiple items are unmet',
    reEvaluationRecommended: 'Re-evaluation recommended after additional preparation',
    blockersToResolve: '{count} blocker(s) need to be resolved',
    itemScores: 'Item Scores',
    blockers: 'Blockers',
    blockersSubtitle: 'Must resolve before release',
    recommendations: 'Recommendations',
    confirmation: 'Confirmation',
    author: 'Author',
    approver: 'Approver',
    savePdf: '📄 Download PDF',
    generating: 'Generating...',
    newEvaluation: '> new evaluation',
    // 구조화된 권고사항
    actionItems: 'Action Items',
    deadline: 'Recommended Deadline',
    owner: 'Owner',
    priorityHigh: 'High',
    priorityMedium: 'Medium',
    priorityLow: 'Low',
    // PDF 다운로드
    pdfDownload: '📄 Download PDF Report',
    pdfDownloading: 'Generating PDF...',
    pdfProOnly: 'Pro plan only',
    pdfUpgradeHint: 'Upgrade to Pro plan to download PDF reports.',
    pdfError: 'Failed to generate PDF. Please try again.',
    maxAiReport: 'View AI Analysis Report',
    maxUpgradeHint: 'Get deeper insights with AI Analysis Report on Max plan',
    // Platform Insights
    platformInsights: 'Platform Insights',
    platformInsightsSubtitle: 'Key considerations by platform',
    keyChanges: 'Key Changes',
    alignedPoints: 'Aligned Points',
    gaps: 'Gaps to Address',
    tips: 'Tips & Notes',
  },
  status: {
    go: 'GO',
    goDesc: 'Release Recommended',
    conditional: 'CONDITIONAL',
    conditionalDesc: 'Conditional Release',
    holding: 'HOLDING',
    holdingDesc: 'Release Holding',
    noGo: 'NO-GO',
    noGoDesc: 'Release Blocked',
  },
  grade: {
    excellent: 'Excellent',
    good: 'Good',
    average: 'Average',
    poor: 'Poor',
    insufficient: 'Insufficient',
  },
  qaValue: {
    verified: 'Verified',
    partiallyVerified: 'Partially Verified',
    partiallyUnverified: 'Partially Unverified',
    unverified: 'Unverified',
    notEntered: 'Not entered',
    partiallyFailed: 'Partially Failed',
    failed: 'Failed',
  },
  footer: {
    tagline: 'Plan Check → Risk Check → Test Check',
    copyright: '© 2025 ReleaseGate. All rights reserved.',
  },
  error: {
    unknown: 'Unknown error',
  },
  language: {
    ko: '한국어',
    en: 'English',
  },
  theme: {
    selector: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
  },
  loadingQuotes: [
    '"Quality is not an act, it is a habit." — Aristotle',
    '"Testing shows the presence, not the absence of bugs." — Dijkstra',
    '"Find bugs before users do. That\'s why we exist." 🐛',
    '"Today\'s test prevents tomorrow\'s outage." 🛡️',
    '"A good QA engineer is not the enemy of developers, but the strongest shield." 🤝',
    '"There is no bug-free code. Just undiscovered ones." 🔍',
    '"A cup of coffee and thorough testing!" ☕',
    '"Test cases are our weapons, bug reports are our medals." 🎖️',
    '"The last line of defense before release. You are the guardian." 🏰',
    '"For perfect quality, we click on." 🖱️',
    '"User smiles are our reward." 😊',
    '"Quality is not negotiable." 💪',
    '"Catching bugs is hunting, preventing them is art." 🎨',
    '"One test saves a thousand support tickets." 📉',
    '"QA engineers\' attention to detail builds service trust." ✨',
  ],
  auth: {
    login: 'Login',
    logout: 'Logout',
    loginWithGoogle: 'Login with Google',
    loginWithGithub: 'Login with GitHub',
    loginWithEmail: 'Login with Email',
    or: 'or',
    quickLoginTitle: 'Quick Login',
    quickLoginDesc: 'Choose a sign-in method.',
    welcomeBack: 'Welcome back!',
    profile: 'Profile',
    loginRequired: 'Login required',
    loginDescription: 'Sign in easily with your Google account.',
    manageSubscription: 'Manage Subscription',
    subscriptionDesc: 'Change payment, plan, or cancel',
    loggingIn: 'Logging in...',
    // Email/Password authentication
    email: 'Email',
    emailPlaceholder: 'example@email.com',
    password: 'Password',
    passwordPlaceholder: 'Enter password',
    passwordConfirm: 'Confirm password',
    passwordConfirmPlaceholder: 'Re-enter password',
    name: 'Name',
    namePlaceholder: 'Enter name (optional)',
    continueWithEmail: 'Continue with email',
    createAccount: 'Create account',
    signUp: 'Sign up',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    forgotPassword: 'Forgot password?',
    resetPassword: 'Reset password',
    setPassword: 'Set password',
    changePassword: 'Change password',
    // OTP verification
    otpTitle: 'Email Verification',
    otpDescription: 'Enter the 6-digit code sent to {email}.',
    otpPlaceholder: '6-digit code',
    otpExpireInfo: 'Code expires in {minutes} minutes.',
    otpResend: 'Resend code',
    otpResendCooldown: 'Resend in {seconds}s',
    otpVerify: 'Verify',
    otpVerifying: 'Verifying...',
    // Password policy
    passwordPolicy: 'Password requirements',
    passwordMinLength: 'At least 8 characters',
    passwordUppercase: 'Contains uppercase',
    passwordLowercase: 'Contains lowercase',
    passwordNumber: 'Contains number',
    passwordSpecial: 'Contains special character',
    passwordMatch: 'Passwords match',
    // Password reset
    resetPasswordTitle: 'Reset Password',
    resetPasswordDescription: 'Enter your email and we will send you a reset link.',
    resetPasswordSent: 'Password reset link sent.',
    resetPasswordSentDescription: 'Check your email and click the link to reset your password.',
    newPassword: 'New password',
    newPasswordConfirm: 'Confirm new password',
    resetPasswordComplete: 'Password has been reset.',
    // Account linking
    accountConnected: 'Account connected with {provider}',
    connectPassword: 'Add password login',
    passwordConnected: 'Password has been set',
    // Error messages
    emailRequired: 'Email is required',
    passwordRequired: 'Password is required',
    invalidEmail: 'Invalid email format',
    invalidPassword: 'Please check password requirements',
    passwordMismatch: 'Passwords do not match',
  },
  coupon: {
    menu: 'Coupon',
    title: 'Coupon',
    subtitle: 'Register a coupon code, then use it from your registered list.',
    codeLabel: 'Coupon code',
    codePlaceholder: 'e.g., WELCOME30',
    register: 'Register',
    registering: 'Registering...',
    registered: 'Coupon registered',
    registeredListTitle: 'Registered coupons',
    noRegistered: 'No registered coupons.',
    use: 'Use',
    using: 'Using...',
    used: 'Used',
    apply: 'Apply',
    applying: 'Applying...',
    applied: 'Coupon applied',
    applyFailed: 'Failed to apply coupon',
    registerFailed: 'Failed to register coupon',
    loadFailed: 'Failed to load coupons',
    close: 'Close',
    errors: {
      COUPON_CODE_REQUIRED: 'Please enter a coupon code.',
      COUPON_INVALID: 'Invalid coupon code.',
      COUPON_NOT_REGISTERED: 'Coupon not registered. Please register it first.',
      COUPON_ALREADY_USED: 'This coupon has already been used.',
      COUPON_INACTIVE: 'This coupon is inactive.',
      COUPON_NOT_STARTED: 'This coupon is not yet available.',
      COUPON_EXPIRED: 'This coupon has expired.',
      COUPON_PLAN_TOO_HIGH: 'Your current plan is higher than the coupon benefit.',
      COUPON_PLAN_MISMATCH: 'This coupon is only available for specific plan users.',
      COUPON_EXHAUSTED: 'This coupon has been fully redeemed.',
      COUPON_BETA_PERIOD: 'Coupons cannot be used during the beta period.',
      COUPON_PLAN_UNKNOWN: 'Unable to verify plan information.',
      USER_NOT_FOUND: 'User not found.',
    },
  },
  terms: {
    title: 'Terms of Service Agreement',
    subtitle: 'Please agree to the following terms to use the service.',
    termsOfService: 'Terms of Service',
    termsOfServiceRequired: 'Agree to Terms of Service (Required)',
    privacyPolicy: 'Privacy Policy',
    privacyPolicyRequired: 'Agree to Privacy Policy (Required)',
    marketingConsent: 'Marketing Communications',
    marketingOptional: 'Agree to receive marketing information (Optional)',
    agreeAll: 'Agree to All',
    continue: 'Agree and Continue',
    cancel: 'Cancel',
    viewDetails: 'View Details',
    lastUpdated: 'Last Updated',
  },
  profile: {
    title: 'My Info',
    email: 'Email',
    emailReadonly: 'Google account email cannot be modified',
    name: 'Name',
    namePlaceholder: 'Enter your name',
    phone: 'Phone Number',
    phonePlaceholder: '234-567-8900',
    company: 'Company/Organization',
    companyPlaceholder: 'e.g., Google, Microsoft, Startup name',
    domain: 'Domain',
    domainPlaceholder: 'e.g., Fintech, E-commerce, Healthcare',
    position: 'Position',
    positionPlaceholder: 'e.g., QA Engineer, PM, Developer',
    evaluationContext: 'This information is used for context during release evaluation.',
    // GitHub integration
    githubIntegrationTitle: 'GitHub Integration',
    githubNotConnectedDesc: 'Connect your GitHub account to read repositories.',
    githubConnectedAs: 'Connected: @{username}',
    githubConnect: 'Connect GitHub',
    githubConnectFailed: 'Failed to connect GitHub',
    githubConnectConfirmTitle: 'Connect GitHub Account',
    githubConnectConfirmDesc: 'GitHub OAuth may proceed using the GitHub account currently signed in on this browser.',
    githubConnectDifferentAccountHint: 'To use a different GitHub account: (1) log out from GitHub and sign in with the desired account, or (2) use another browser profile/incognito window.',
    githubUseCurrentAccount: 'Continue with current GitHub account',
    githubConnecting: 'Connecting...',
    githubOpenLogoutPage: 'Open GitHub logout page',
    githubDisconnect: 'Disconnect',
    githubDisconnecting: 'Disconnecting...',
    githubDisconnectFailed: 'Failed to disconnect GitHub',
    githubLoading: 'Loading...',
    githubCommitReviewOpen: 'Review commit changes',
    // GitHub Organization access
    githubOrgAccessQuestion: 'Can\'t see organization repos?',
    githubOrgAccessSettings: 'GitHub permissions →',
    githubOrgAccessHint: 'Click and Grant access to your Organization.',
    save: 'Save',
    saving: 'Saving...',
    saveSuccess: 'Profile saved successfully',
    saveFailed: 'Failed to save profile',
    profileImage: 'Profile Image',
    uploadImage: 'Upload',
    changeImage: 'Change',
    deleteImage: 'Delete',
    uploading: 'Uploading...',
    imageHint: 'JPEG, PNG, GIF, WebP (max 5MB)',
    imageTypeError: 'Unsupported file type. (JPEG, PNG, GIF, WebP only)',
    imageSizeError: 'File size must be less than 5MB.',
    imageUploadFailed: 'Failed to upload image.',
    imageDeleteFailed: 'Failed to delete image.',
    storageNotAvailable: 'Image upload is not available in local environment.',
    deleteAccount: 'Delete Account',
    deleteWarningTitle: '⚠️ Are you sure you want to delete your account?',
    deleteWarning: 'Deleting your account will immediately disable access and remove your service data (evaluation history, team info, etc.). Some information may be retained for up to 1 month (and payment-related records for 5 years) in accordance with our Privacy Policy and applicable laws, and then securely deleted.',
    confirmDelete: 'Delete Account',
    deleting: 'Deleting...',
    deleteFailed: 'Failed to delete account',
    cancel: 'Cancel',
    farewellTitle: 'We\'ll miss you',
    farewellMessage: 'Your account has been deleted. Thanks for using our service.',
    farewellSubMessage: 'Come back anytime — we\'ll be happy to see you again.',
    farewellCta: 'OK',
    farewellTogetherLine: 'We spent D+{days} days together.',
    farewellSeeYouLine: 'Hope to see you again someday.',
    leaveTeamFirstTitle: '👥 You belong to a team',
    leaveTeamFirstMessage: 'You must leave your team before deleting your account.',
    leaveTeamFirstHint: 'Please leave the team from Profile > Team Management and try again.',
    understood: 'Got it',
    // Member since (days together)
    memberSince: 'Member Since',
    daysTogether: 'Our Journey',
    daysTogetherUnit: ' days',
    milestone7: '🌱 Fresh Sprout',
    milestone30: '🌿 Monthly Mate',
    milestone100: '🌳 100-Day Partner',
    milestone365: '🎂 1st Anniversary',
    milestone1000: '🏆 Legendary Member',
    nextMilestone: 'Next milestone',
    daysRemaining: '{days} days left',
  },
  rating: {
    title: 'Was this helpful?',
    subtitle: 'Just 10 seconds! Your feedback helps us improve the service 💪',
    starLabel: ' star(s)',
    rating1: '😢 Very disappointing',
    rating2: '😕 Could be better',
    rating3: '😐 It\'s okay',
    rating4: '😊 Good!',
    rating5: '🤩 Excellent!',
    later: 'Maybe later',
    neverAsk: 'Don\'t ask again',
    thankYouHigh: 'Thank you! 🎉',
    thankYouLow: 'Thanks for your feedback',
    feedbackPromptHigh: 'We\'d love to hear what you liked!',
    feedbackPromptLow: 'What could we improve? We\'re listening.',
    feedbackPlaceholder: 'Share your thoughts... (optional)',
    back: 'Back',
    submit: 'Submit',
    submitting: 'Submitting...',
    skipFeedback: 'Submit rating only',
    thankYouComplete: 'Thanks for your feedback! We\'ll keep improving ✨',
  },
  pricing: {
    title: 'Pricing',
    subtitle: 'The most affordable investment to improve your release quality',
    viewPricing: 'View Pricing',
    // Plan status
    currentPlanStatus: 'Current Plan Status',
    currentPlan: 'Current Plan',
    beta: 'Beta',
    betaPeriod: 'Beta Period',
    freeAccess: 'Free Access 🎉',
    started: 'Started',
    nextBilling: 'Next Billing',
    expiresOn: 'Expires On',
    afterBeta: 'After Beta',
    subscriptionExpiresWarning: 'Your subscription expires on {date}. You\'ll be switched to Free plan starting {nextDate}.',
  },
  history: {
    title: 'Evaluation History',
    subtitle: 'Total {count} (max {limit})',
    empty: 'No evaluation history yet.',
    loadError: 'Failed to load history.',
    retry: 'Retry',
    detail: 'Evaluation Detail',
    reEvaluate: 'Re-evaluate with this data',
    viewHistory: 'History',
  },
  team: {
    title: 'Team Management',
    createTeam: 'Create Team',
    teamName: 'Team Name',
    teamNamePlaceholder: 'e.g., QA Team, Product Team',
    create: 'Create',
    creating: 'Creating...',
    members: 'Members',
    memberCount: '{count} / {max}',
    pendingInvites: 'Pending Invites',
    inviteMember: 'Invite Member',
    emailPlaceholder: 'Email address to invite',
    roleManager: 'Manager',
    roleMember: 'Member',
    roleOwner: 'Owner',
    roleLead: 'Owner(Lead)',
    invite: 'Invite',
    inviting: 'Inviting...',
    cancelInvite: 'Cancel Invite',
    removeMember: 'Remove Member',
    changeRole: 'Change Role',
    leaveTeam: 'Leave Team',
    deleteTeam: 'Delete Team',
    noTeam: 'No team yet',
    noTeamDesc: 'Create a team or join one by accepting an invitation.',
    teamRequired: 'Team Plan Required',
    teamRequiredDesc: 'Team features are only available on the Team plan.',
    maxMembers: 'Max {max} members',
    expires: 'Expires in {days} days',
    expired: 'Expired',
    inviteSent: 'Invitation sent',
    memberRemoved: 'Member removed',
    inviteCancelled: 'Invite cancelled',
    roleChanged: 'Role changed',
    leftTeam: 'Left the team',
    teamDeleted: 'Team deleted',
    confirmRemove: 'Are you sure you want to remove this member?',
    confirmLeave: 'Are you sure you want to leave the team?',
    confirmDelete: 'Delete Team',
    confirmDeleteDesc: 'Deleting the team will disconnect all members. This action cannot be undone.',
    cancel: 'Cancel',
    confirm: 'Confirm',
    viewTeam: 'Team',
    // Transfer ownership
    transferOwnership: 'Transfer Ownership',
    transferOwnershipDesc: 'Transfer lead (owner) role to another team member. You will become a manager.',
    transferTo: 'Select new lead',
    confirmTransfer: 'Confirm Transfer',
    confirmTransferDesc: 'Are you sure you want to transfer lead role to {name}? This action cannot be undone.',
    ownershipTransferred: 'Ownership has been transferred',
    // Extra seats
    addSeats: 'Add More Seats',
    addSeatsDesc: 'Purchase additional seats to add more team members.',
    seatsPerPurchase: 'Add 5 seats',
    maxSeatsReached: 'Maximum seats reached (50 members)',
    purchaseSeats: 'Purchase Seats',
    purchasing: 'Redirecting to checkout...',
  },
  apiKeys: {
    title: 'API Keys',
    subtitle: 'Automate release evaluations via API',
    createKey: 'Create New API Key',
    keyName: 'Key Name',
    keyNamePlaceholder: 'e.g., Production, Development',
    create: 'Create',
    creating: 'Creating...',
    noKeys: 'No API keys yet',
    noKeysDesc: 'Create an API key to use the release evaluation API.',
    teamRequired: 'Team Plan Required',
    teamRequiredDesc: 'API keys are only available on the Team plan.',
    keyCreated: 'API Key Created!',
    keyCreatedWarning: 'This key will not be shown again. Copy it now.',
    copyKey: 'Copy',
    copied: 'Copied!',
    dailyUsage: 'Daily Usage',
    lastUsed: 'Last Used',
    never: 'Never used',
    revokeKey: 'Revoke',
    confirmRevoke: 'Are you sure you want to revoke this API key? Revoked keys cannot be used again.',
    keyRevoked: 'API key revoked',
    maxKeysReached: 'You can create up to {max} API keys',
    viewApiKeys: 'API Keys',
    apiDocumentation: 'View API Docs',
    memberViewOnly: 'Only team owners can create API keys. You can view existing keys.',
    usageGuide: 'API Usage Guide',
    usageGuideDesc: 'Learn how to use API keys and available endpoints.',
  },
  downgrade: {
    // Common
    planExpired: 'Your subscription has expired',
    planDowngraded: 'Your plan has been changed',
    upgradeToAccess: 'Upgrade your plan to access this feature',
    upgradePlan: 'Upgrade Plan',
    // Team features
    teamFrozen: 'Team features are disabled',
    teamFrozenDesc: 'Team features are paused because the team owner\'s Team subscription has expired.',
    teamFrozenOwnerDesc: 'Renew your Team plan to re-enable team features.',
    teamDataPreserved: 'Your team data is safely preserved',
    teamMembersAffected: '{count} team members are affected',
    // API Keys
    apiKeysFrozen: 'API keys are disabled',
    apiKeysFrozenDesc: 'API key access is paused because your Team subscription has expired.',
    apiKeysDataPreserved: 'Your keys are preserved and will work again when you resubscribe',
    // History
    historyReadOnly: 'History is read-only',
    historyReadOnlyDesc: 'New evaluations will not be saved to history on your current plan.',
    historyLimitReached: 'History storage limit reached',
    // Feature restrictions
    featureLocked: 'This feature is not available on your current plan',
    pdfLocked: 'PDF download is available on Pro and above plans',
    // Quota
    quotaExceeded: 'Monthly evaluation limit exceeded',
    quotaResetDate: 'Resets on {date}',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Plan info & usage overview',
    backToHome: '← Back to Home',
    currentPlan: 'Current Plan',
    planFeatures: 'Plan Features',
    usage: 'Usage',
    monthlyEvaluations: 'Monthly Evaluations',
    unlimited: 'Unlimited',
    remaining: 'remaining',
    used: 'used',
    limits: 'Limits',
    testCasesPerEval: 'Test cases per evaluation',
    planningChars: 'Planning characters',
    historyStorage: 'History storage',
    pdfDownload: 'PDF Download',
    apiAccess: 'API Access',
    teamFeature: 'Team Feature',
    available: 'Available',
    notAvailable: 'Not Available',
    items: '',
    chars: ' chars',
    upgradePrompt: 'Want more features?',
    upgradeCta: 'Upgrade Plan',
    teamInfo: 'Team Info',
    teamName: 'Team Name',
    teamRole: 'Role',
    teamMembers: 'Members',
    // Trends & Dashboard
    personalTrend: 'Personal Trends',
    teamTrend: 'Team Trends',
    teamDashboard: 'Team Dashboard',
    teamDashboardTitle: 'Team Dashboard',
    teamDashboardDesc: 'View your entire team\'s release quality at a glance',
    viewTeamDashboard: 'View Team Dashboard',
    teamDashboardRequireTeam: 'Available on Team plan only',
    teamMemberStats: 'Member Statistics',
    teamTrendAnalysis: 'Team Trend Analysis',
  },
  demo: {
    title: 'Demo Mode',
    viewResult: '> View Demo Result',
    loginPrompt: 'Login to input your own data and get AI evaluation',
    resultBanner: 'Demo Result',
    resultDescription: 'Login to get AI evaluation with your own data',
  },
  statistics: {
    title: 'Evaluation Statistics',
    subtitle: 'Data from the last {days} days',
    description: 'View daily trends and status distribution',
    viewButton: 'View Details',
    loadError: 'Failed to load statistics',
    totalEvaluations: 'Total Evaluations',
    avgScore: 'Avg Score',
    dailyTrend: 'Daily Evaluation Trend',
    statusDistribution: 'Status Distribution',
    noData: 'No data',
    periodDays: 'Period',
  },
  errorPage: {
    goHome: 'Go Home',
    retry: 'Retry',
    viewPricing: 'View Pricing',
    supportContact: 'If the issue persists, contact us at {email}.',
    401: {
      title: 'Login Required',
      description: 'You must be logged in to continue.',
      hint: 'Please log in and try again. (This can also happen when your session has expired.)',
    },
    402: {
      title: 'Payment Required',
      description: 'A paid subscription is required to access this feature.',
      hint: 'Your free trial may have expired, or there might be an issue with your payment information.',
    },
    404: {
      title: 'Page Not Found',
      description: 'The page you requested does not exist or has been moved.',
      hint: 'Check if the URL is correct, or start fresh from the home page.',
    },
    429: {
      title: 'Too Many Requests',
      description: 'You have sent too many requests in a short period. Please try again later.',
      hint: 'You have reached the API rate limit. Try again in a few minutes or consider upgrading your plan.',
    },
    500: {
      title: 'Internal Server Error',
      description: 'Sorry, an unexpected error occurred on the server.',
      hint: 'Please try again later. If the problem persists, contact support.',
    },
    505: {
      title: 'Not Supported',
      description: 'The requested HTTP version or protocol is not supported.',
      hint: 'Update your browser to the latest version, or try a different browser.',
    },
  },
  welcome: {
    title: 'Welcome to ReleaseGate!',
    subtitle: 'Reduce uncertainty in release decisions with our AI-powered tool.',
    question: 'How will you use ReleaseGate?',
    individual: "I'll use it personally",
    individualDesc: 'For personal projects or solo QA work',
    team: "I'll use it with my team",
    teamDesc: 'Share release decisions with your team',
    justBrowse: 'Just browsing for now →',
    back: 'Back',
    selectPlan: 'Choose your plan',
    betaBenefit: 'Beta Benefit',
    betaBenefitDesc: 'All features are free during the beta period!',
    decideLater: "I'll decide later, just browsing for now",
    getStarted: 'Get Started',
    processing: 'Processing...',
    forTeams: 'FOR TEAMS',
    popular: 'POPULAR',
    individualPlans: 'Individual Plans',
    teamPlan: 'Team Plan',
  },
  suggestedTestcases: {
    title: 'AI Suggested Checklist',
    subtitle: 'AI suggests pre-release checklist based on planning intent, risks, and commits',
    generateButton: 'Generate Checklist',
    generating: 'Generating checklist...',
    regenerate: 'Regenerate',
    clear: 'Clear',
    coreCategory: 'Core',
    edgeCategory: 'Edge',
    priorityHigh: 'High',
    priorityMedium: 'Medium',
    priorityLow: 'Low',
    statusPass: 'Pass',
    statusFail: 'Fail',
    statusNA: 'N/A',
    statusNT: 'N/T',
    selectStatus: 'Select Status',
    source: 'Source',
    sourceRisk: 'Risk',
    sourcePlanning: 'Planning',
    sourceCommit: 'Commit',
    noTestcases: 'No checklist items generated yet',
    generateHint: 'Enter planning intent, risks, or GitHub commits, then generate checklist',
    summary: 'Summary',
    passCount: 'Pass',
    failCount: 'Fail',
    naCount: 'N/A',
    ntCount: 'Not Checked',
    addMemo: 'Add memo',
    hideMemo: 'Hide memo',
    memoPlaceholder: 'Add notes about this checklist item...',
    memo: 'Memo',
    // Assignees
    author: 'Author',
    tester: 'Tester',
    modifier: 'Modifier',
    authorPlaceholder: 'Author name',
    testerPlaceholder: 'Tester name',
    modifiedAt: 'Modified',
    assignees: 'Assignees',
    hideAssignees: 'Cancel',
    editHistory: 'Edit History',
    before: 'Before',
    after: 'After',
    // Plan restrictions
    aiGenerateLocked: 'AI Checklist Generation',
    aiGenerateMaxTeamOnly: 'Available for Max/Team plans only',
    aiGenerateProRegister: 'Pro plan: Registration required',
    aiGenerateFreeLocked: 'Upgrade to unlock',
    registerToUse: 'Register',
    upgradePlan: 'Upgrade Plan',
  },
  
  autoLogout: {
    title: 'Session Expiring',
    description: 'You will be automatically logged out due to inactivity.',
    remaining: 'Time remaining',
    extend: 'Stay Logged In',
    logoutNow: 'Log Out',
    hint: 'For security, you will be automatically logged out after 30 minutes of inactivity.',
    loggedOutTitle: 'You have been logged out',
    loggedOutDescription: 'You have been automatically logged out for security. Please log in again to continue.',
    loginAgain: 'Log In Again',
    closeModal: 'Close',
  },

  home: {
    // Logged in user home
    startEvaluation: 'Start Evaluation',
    quickActions: 'Quick Actions',
    recentActivity: 'Recent Activity',
    noRecentActivity: 'No evaluation history yet',
    viewAllHistory: 'View All History',
    // Guest landing
    heroTitle: 'AI-Powered\nRelease Go/No-Go',
    heroSubtitle: 'Input planning intent, risks, and QA results\nand let AI decide if you\'re ready to release',
    tryDemo: 'Try Demo',
    signUpFree: 'Start Free',
    feature1Title: '3-Step Validation',
    feature1Desc: 'Plan → Risk → Test sequential check',
    feature2Title: 'AI Analysis',
    feature2Desc: 'Context-aware precise evaluation',
    feature3Title: 'Team Collaboration',
    feature3Desc: 'Team-wide release quality dashboard',
    trustedByOptions: [
      'Built for QA teams',
      'Start your journey today',
      'Experience smarter release validation',
    ],
    // Days together
    daysTogether: 'Days Together',
    daysTogetherSuffix: ' days',
    // Badges
    badges: 'Earned Badges',
    badgeNewbie: '🌱 Newbie',
    badgeExplorer: '🔍 Explorer',
    badgeRegular: '⭐ Regular',
    badgeVeteran: '🏅 Veteran',
    badgeLegend: '🏆 Legend',
    noBadges: 'No badges yet',
    badgeUnlocked: 'Badge Unlocked!',
    // Quick stats
    totalEvaluations: 'Total Records',
    thisMonth: 'This Month',
    avgScore: 'Avg Score',
  },

  testcaseManager: {
    title: 'Test Case Manager',
    subtitle: 'Create and manage your team\'s test cases',
    createNew: 'New Test Case',
    importFile: 'Import File',
    searchPlaceholder: 'Search test cases...',
    filterAll: 'All',
    filterActive: 'Active',
    filterArchived: 'Archived',
    sortNewest: 'Newest',
    sortOldest: 'Oldest',
    sortName: 'Name',
    noTestcases: 'No test cases',
    noTestcasesDesc: 'Create a new test case or import from file',
    // Test case fields
    category: 'Category',
    priority: 'Priority',
    status: 'Status',
    lastUpdated: 'Last Updated',
    createdAt: 'Created At',
    description: 'Description',
    expectedResult: 'Expected Result',
    actualResult: 'Actual Result',
    // Actions
    edit: 'Edit',
    delete: 'Delete',
    duplicate: 'Duplicate',
    archive: 'Archive',
    restore: 'Restore',
    useInEvaluation: 'Use in Evaluation',
    // Create/Edit form
    createTitle: 'Create New Test Case',
    editTitle: 'Edit Test Case',
    titleLabel: 'Title',
    titlePlaceholder: 'Enter test case title',
    descriptionPlaceholder: 'Describe the test scenario...',
    expectedResultPlaceholder: 'Enter expected result...',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    // Categories
    categoryCore: 'Core',
    categoryEdge: 'Edge',
    categoryRegression: 'Regression',
    categorySmoke: 'Smoke',
    // Status
    statusDraft: 'Draft',
    statusActive: 'Active',
    statusPassed: 'Passed',
    statusFailed: 'Failed',
    statusSkipped: 'Skipped',
    // Mockup notice
    mockupTitle: '🚧 Coming Soon',
    mockupDesc: 'Test case management is coming soon!\nBelow is a feature preview.',
    comingSoon: 'Coming Soon',
  },

  flowGenerator: {
    title: '🔄 Flow Testcase Generator',
    subtitle: 'Auto-generate testcases from screen flow',
    description: 'Enter screens and transitions to auto-generate CSV files for screens, transitions, assertions, contexts, and testcases.',
    // Tabs
    inputTab: 'Input',
    previewTab: 'Preview',
    downloadTab: 'Download',
    // Screen input
    screensTitle: 'Screens',
    addScreen: 'Add Screen',
    screenId: 'Screen ID',
    screenName: 'Screen Name',
    isMenu: 'Menu',
    // Transitions
    transitionsTitle: 'Transitions',
    addTransition: 'Add Transition',
    fromScreen: 'From Screen',
    toScreen: 'To Screen',
    triggerLabel: 'Trigger (button/link)',
    // Options
    optionsTitle: 'Options',
    testidPrefix: 'data-testid Prefix',
    autoGenerateTestids: 'Auto-generate testids',
    autoGenerateAssertions: 'Auto-generate assertions',
    // Actions
    generateCSV: 'Generate CSV',
    generating: 'Generating...',
    downloadZip: 'Download ZIP',
    downloading: 'Downloading...',
    importJSON: 'Import JSON',
    exportJSON: 'Export JSON',
    // Stats
    statsTitle: 'Stats',
    totalScreens: 'Screens',
    totalTransitions: 'Transitions',
    totalTestcases: 'Testcases',
    // Warnings/Errors
    warningsTitle: 'Warnings',
    noScreensError: 'Please add at least 1 screen.',
    noTransitionsError: 'Please add at least 1 transition.',
    needTwoScreens: 'At least 2 screens are required to add a transition.',
  },

  releaseTarget: {
    title: 'Evaluation Target',
    frontend: 'Frontend',
    frontendDesc: 'Web/Mobile UI',
    backend: 'Backend',
    backendDesc: 'API/Service',
    fullstack: 'Fullstack',
    fullstackDesc: 'Front + Back',
    infra: 'Infra',
    infraDesc: 'Infrastructure/DevOps',
    automation: 'Automation',
    automationDesc: 'Automation/Bot',
  },

  backendChecks: {
    title: 'Backend Checklist',
    subtitle: 'Required checklist for backend release',
    // API Contract
    apiContract: 'API Contract/Documentation',
    apiContractDesc: 'API schema and contract changes',
    schemaChanged: 'Schema Changed (OpenAPI/Response)',
    breakingChange: 'Breaking Change (Field removal/Type change)',
    versionStrategy: 'Version Strategy',
    versionStrategyPlaceholder: 'e.g., v1 → v2, deprecate in 3 months',
    errorModelDefined: 'Error Model Defined',
    // Auth/Security
    authSecurity: 'Auth/Security',
    authSecurityDesc: 'Authentication, authorization, security checks',
    authChanged: 'Auth Changed (JWT, API Key, scope)',
    rateLimitConfigured: 'Rate Limit Configured',
    abuseProtection: 'Abuse Protection Applied',
    piiLoggingSafe: 'PII Log Masking Applied',
    // DB/Migration
    dbMigration: 'DB/Migration',
    dbMigrationDesc: 'Database and migration checks',
    migrationExists: 'Migration Script Exists',
    rollbackPossible: 'Rollback Possible',
    backwardCompatible: 'Backward Compatible',
    indexPerformanceChecked: 'Index/Query Performance Checked',
    // Reliability
    reliability: 'Reliability',
    reliabilityDesc: 'Fault tolerance and stability checks',
    timeoutConfigured: 'Timeout Configured',
    retryLogic: 'Retry Logic Exists',
    circuitBreaker: 'Circuit Breaker Applied',
    externalDependencyFallback: 'External Dependency Fallback',
    externalDependencyFallbackPlaceholder: 'e.g., Use local cache when Redis is down',
    // Observability
    observability: 'Observability',
    observabilityDesc: 'Monitoring and alerting checks',
    loggingReady: 'Logging Ready',
    metricsReady: 'Metrics Ready',
    tracingReady: 'Tracing Ready',
    alertingConfigured: 'Alerting Configured',
    sentryIntegrated: 'Sentry Integrated',
    // Performance
    performance: 'Performance',
    performanceDesc: 'Performance targets and testing',
    p95TargetMs: 'p95 Target (ms)',
    p99TargetMs: 'p99 Target (ms)',
    loadCondition: 'Load Condition',
    loadConditionPlaceholder: 'e.g., 100 concurrent users, 1000 RPS',
    loadTestResult: 'Load Test Result',
    loadTestResultPlaceholder: 'e.g., p95 120ms, error rate 0.01%',
    // Common
    yes: 'Yes',
    no: 'No',
    notSet: 'Not Set',
  },

  // Settings
  settings: {
    title: 'Settings',
    // Tabs
    accountLinking: 'Account Linking',
    accountManagement: 'Account',
    security: 'Security',
    display: 'Display',
    mfa: 'MFA',
    notifications: 'Notifications',
    termsTab: 'Terms',
    // Account Linking
    googleAccount: 'Google Account',
    githubAccount: 'GitHub Account',
    connected: 'Connected',
    notConnected: 'Not Connected',
    connect: 'Connect',
    disconnect: 'Disconnect',
    connecting: 'Connecting...',
    disconnecting: 'Disconnecting...',
    connectFailed: 'Failed to connect',
    disconnectFailed: 'Failed to disconnect',
    // Account Management
    deleteAccount: 'Delete Account',
    deleteAccountDesc: 'Deleting your account will permanently remove all data and cannot be undone.',
    deleteAccountButton: 'Delete Account',
    changePassword: 'Change Password',
    changePasswordDesc: 'Regularly update your password to keep your account secure.',
    changePasswordButton: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    passwordChanged: 'Password changed successfully.',
    passwordChangeFailed: 'Failed to change password.',
    passwordMismatch: 'New passwords do not match.',
    // Security
    loginHistory: 'Login History',
    loginHistoryDesc: 'View your recent login activity.',
    activeSessions: 'Active Sessions',
    activeSessionsDesc: 'Manage devices that are currently logged in.',
    currentSession: 'Current Session',
    logoutAllSessions: 'Log out all sessions',
    noLoginHistory: 'No login history.',
    device: 'Device',
    location: 'Location',
    lastAccess: 'Last Access',
    securityComingSoon: 'Security features are coming soon.',
    // Display Settings
    languageSetting: 'Language',
    languageSettingDesc: 'Select your interface language.',
    themeSetting: 'Theme',
    themeSettingDesc: 'Choose your display theme.',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    timezoneSetting: 'Timezone',
    timezoneSettingDesc: 'Select timezone for date and time display.',
    timezoneAuto: 'Auto (Browser setting)',
    // MFA
    mfaTitle: 'Enable MFA',
    mfaDescription: 'Multi-Factor Authentication (MFA) adds an extra layer of security to your account.',
    mfaEnabled: 'Enable MFA',
    emailOtp: 'Email OTP',
    emailOtpDesc: 'Receive a verification code via email when signing in.',
    smsOtp: 'SMS OTP',
    smsOtpDesc: 'Receive a verification code via SMS when signing in.',
    smsOtpDisabled: 'Coming soon',
    totp: 'TOTP Authenticator',
    totpDesc: 'Verify using a code from an authenticator app.',
    totpApps: 'Google Authenticator, Authy, etc.',
    totpDisabled: 'Coming soon',
    // Notifications
    notificationsNotImplemented: 'Notification settings are coming soon.',
    emailNotifications: 'Email Notifications',
    emailNotificationsDesc: 'Choose which email notifications you want to receive.',
    notifyEvaluation: 'Evaluation Complete',
    notifyEvaluationDesc: 'Receive Go/No-Go evaluation results via email.',
    notifyTeamInvite: 'Team Invitation',
    notifyTeamInviteDesc: 'Get notified when you receive a team invitation.',
    notifyTeamActivity: 'Team Activity',
    notifyTeamActivityDesc: 'Get notified when team members complete evaluations.',
    notifySubscription: 'Subscription',
    notifySubscriptionDesc: 'Payment failures, renewal reminders, and plan changes.',
    notifySecurity: 'Security',
    notifySecurityDesc: 'New device logins and password changes.',
    notifyNewsletter: 'Newsletter',
    notifyNewsletterDesc: 'Software news delivered morning, noon, and evening.',
    notifyProductUpdate: 'Product Updates',
    notifyProductUpdateDesc: 'New features and improvements.',
    // Terms
    termsTitle: 'Terms & Agreements',
    termsDescription: 'View and manage your consent to terms and policies.',
    termsOfService: 'Terms of Service',
    termsOfServiceDesc: 'Required terms for using the service.',
    privacyPolicy: 'Privacy Policy',
    privacyPolicyDesc: 'Required consent for collecting and using personal information.',
    marketingConsent: 'Marketing Communications',
    marketingConsentDesc: 'Receive updates about events and promotions.',
    required: 'Required',
    optional: 'Optional',
    agreed: 'Agreed',
    withdrawn: 'Not agreed',
    termsAgreedToast: 'You have agreed to the Terms of Service.',
    termsWithdrawnToast: 'You have withdrawn consent for Terms of Service.',
    privacyAgreedToast: 'You have agreed to the Privacy Policy.',
    privacyWithdrawnToast: 'You have withdrawn consent for Privacy Policy.',
    marketingAgreedToast: 'You have agreed to receive marketing communications.',
    marketingWithdrawnToast: 'You have withdrawn consent for marketing communications.',
    // Buttons
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
  },

  criteria: {
    title: 'Release Criteria Settings',
    description: 'Customize your Go/Conditional/Holding/No-Go decision criteria.',
    scoreThresholds: 'Score Thresholds',
    goThreshold: 'GO Minimum Score',
    conditionalThreshold: 'Conditional Minimum Score',
    holdingThreshold: 'Holding Minimum Score',
    belowHolding: 'Below this score = No-Go',
    blockerRules: 'Blocker Rules',
    blockerDescription: 'If checked items are present in requirements but exception/risk handling is not mentioned, it will be marked as No-Go.',
    customBlockers: 'Custom Blockers',
    addCustomBlocker: 'Add Custom Blocker',
    keywordPlaceholder: 'Keyword (e.g., HIPAA)',
    messagePlaceholder: 'Blocker message (e.g., HIPAA non-compliance)',
    additionalRules: 'Additional Rules',
    testFailDowngrade: 'Downgrade on Test Failure',
    testFailDowngradeDesc: 'Downgrade GO → Conditional if any test case fails',
    nfrMissingHolding: 'Holding on Missing NFR',
    nfrMissingHoldingDesc: 'Force Holding if non-functional requirements are missing',
    acZeroNogo: 'No-Go on Zero AC Score',
    acZeroNogoDesc: 'Force No-Go if Acceptance Criteria score is 0',
    domainSelection: 'Domain Selection',
    domainDescription: 'Select your domain to get AI-powered recommendations.',
    getRecommendation: 'Get AI Recommendation',
    applyRecommendation: 'Apply Recommendation',
    recommendationReason: 'Recommendation Reason',
    save: 'Save',
    reset: 'Reset to Default',
    resetConfirm: 'Reset settings to default values?',
    saveSuccess: 'Release criteria saved successfully.',
    resetSuccess: 'Reset to default values.',
    validationError: 'Score thresholds must be in order: GO > Conditional > Holding.',
    domains: {
      fintech: 'Fintech/Finance',
      ecommerce: 'E-commerce',
      healthcare: 'Healthcare/Medical',
      social: 'Social/Community',
      saas: 'B2B SaaS',
      gaming: 'Gaming',
      media: 'Media/Content',
      education: 'Education/EdTech',
      other: 'Other',
    },
    blockers: {
      payment: 'Payment',
      personal_info: 'Personal Info',
      auth: 'Authentication',
      webhook: 'Webhook',
      delete: 'Delete',
    },
  },
};

